import 'dayjs/locale/sv';

import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Activity, CheckCircle, Clock, Download, Tag, Users } from 'react-feather';

import { DashSpinner, Link, Tags, Typography, Wrapper } from '@/components';
import { useAuthUser } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { useCampaign } from '@/hooks/client';
import { PlatformIcon } from '@/utils/platformIcon';
import { prettifyNumber } from '@/utils/prettifyNumber';
import { TranslatedLink } from '@/utils/translatedComponents';

import styles from './CampaignPageList.module.css';

export interface CampaignPageListProps {
  campaignId: string;
}

export function CampaignPageList({ campaignId }: CampaignPageListProps) {
  const user = useAuthUser();
  const { t } = useTranslation();

  const { data = {}, isLoading } = useCampaign(campaignId);

  const memoData = useMemo(() => data as Api.Client.Campaign, [data]);

  const memoBriefPdfs = memoData.brief?.files?.filter((file) => file.url.includes('.pdf')) || [];

  if (!user?.is_client) return null;

  return (
    <Wrapper isLoading={isLoading} heading={memoData.name} campaignId={campaignId}>
      {memoData.description && (
        <div className={styles.campaignPageListDescription}>
          <Typography sizes={['body']}>{memoData.description}</Typography>
        </div>
      )}

      <div className={styles.campaignPageListSummary}>
        <div className={styles.campaignPageListSummaryItem}>
          <div className={styles.campaignPageListSummaryItemContent}>
            <div className={styles.campaignPageListSummaryItemContentLabel}>
              <Typography sizes={['label-secondary']}>
                {t('campaignPageList.totalInfluencers')}
              </Typography>
            </div>

            <Typography sizes={['body', 'bold']}>
              <div className={styles.campaignPageListSummaryItemContentValue}>
                <Users size={20} />
                {memoData.total_influencer_count || 0}
              </div>
            </Typography>
          </div>
          <div className={styles.campaignPageListSummaryItemContent}>
            <div className={styles.campaignPageListSummaryItemContentLabel}>
              <Typography sizes={['label-secondary']}>
                {t('campaignPageList.totalBeats')}
              </Typography>
            </div>

            <Typography sizes={['body', 'bold']}>
              <div className={styles.campaignPageListSummaryItemContentValue}>
                <Activity size={20} />
                {memoData.beats?.length}
              </div>
            </Typography>
          </div>
          <div className={styles.campaignPageListSummaryItemContent}>
            <div className={styles.campaignPageListSummaryItemContentLabel}>
              <Typography sizes={['label-secondary']}>{t('campaignPageList.startDate')}</Typography>
            </div>

            <Typography sizes={['body', 'bold']}>
              <div className={styles.campaignPageListSummaryItemContentValue}>
                <Clock size={20} />
                {memoData.date_start ? dayjs(memoData.date_start).format('YYYY-MM-DD') : '-'}
              </div>
            </Typography>
          </div>
          <div className={styles.campaignPageListSummaryItemContent}>
            <div className={styles.campaignPageListSummaryItemContentLabel}>
              <Typography sizes={['label-secondary']}>{t('campaignPageList.endDate')}</Typography>
            </div>

            <Typography sizes={['body', 'bold']}>
              <div className={styles.campaignPageListSummaryItemContentValue}>
                <Clock size={20} />
                {memoData.date_end ? dayjs(memoData.date_end).format('YYYY-MM-DD') : '-'}
              </div>
            </Typography>
          </div>
          <div className={styles.campaignPageListSummaryItemContent}>
            <div className={styles.campaignPageListSummaryItemContentLabel}>
              <Typography sizes={['label-secondary']}>{t('campaignPageList.budget')}</Typography>
            </div>

            <Typography sizes={['body', 'bold']}>
              <div className={styles.campaignPageListSummaryItemContentValue}>
                <Tag size={20} />
                {prettifyNumber(memoData.budget?.amount)} {memoData.budget?.currency || '-'}
              </div>
            </Typography>
          </div>
        </div>
      </div>

      {memoData.todos && memoData.todos.length > 0 && (
        <div className={styles.campaignPageListStatus}>
          <div className={styles.campaignPageListStatusHeading}>
            <Typography sizes={['h4']}>{t('campaignPageList.status')}</Typography>
          </div>

          <div className={styles.campaignPageListStatusList}>
            {memoData.todos.map((todo) => (
              <div
                key={`todo-${todo.name}-${todo.status}`}
                className={styles.campaignPageListStatusItem}
              >
                <div className={styles.campaignPageListStatusIcon}>
                  {todo.status === 'DONE' && <CheckCircle size={20} color="var(--color-green)" />}
                  {todo.status === 'ONGOING' && (
                    <DashSpinner size={20} color="var(--color-green)" />
                  )}
                  {todo.status === 'PLANNED' && <Clock size={20} color="var(--color-gray-75)" />}
                </div>

                <Typography sizes={['label']}>{todo.name}</Typography>
              </div>
            ))}
          </div>
        </div>
      )}

      {!!memoData.beats?.length && (
        <div className={styles.campaignPageListBeats}>
          <div className={styles.campaignPageListBeatsHeading}>
            <Typography sizes={['h4']}>{t('campaignPageList.headingBeats')}</Typography>
          </div>

          {memoData.beats.map((beat, i) => (
            <TranslatedLink
              href={`/connect/campaigns/${campaignId}/beat/${beat.beat_id}`}
              key={`beat-${beat.beat_id}`}
            >
              <div className={styles.campaignPageListBeatsItem}>
                <div className={styles.campaignPageListBeatsItemNumber}>{i + 1}</div>

                <div className={styles.campaignPageListBeatsItemContent}>
                  <div className={styles.campaignPageListBeatsItemContentLeft}>
                    <div className={styles.campaignPageListBeatsItemContentLeftIcon}>
                      {PlatformIcon(beat.platform, 20)}
                    </div>
                    <div className={styles.campaignPageListBeatsItemContentLeftName}>
                      <div className={styles.campaignPageListBeatsItemContentLeftNameType}>
                        <Typography sizes={['label']}>{beat.beat_name}</Typography>
                      </div>
                    </div>
                  </div>

                  <div className={styles.campaignPageListBeatsItemContentRight}>
                    <div>
                      {beat.type === 'IGC' ? (
                        <Typography sizes={['label']}>
                          {t('campaignPageList.draftDeadline')}
                          {': '}
                          {beat.date_start ? dayjs(beat.date_start).format('YYYY-MM-DD') : '-'}
                        </Typography>
                      ) : beat.type === 'IGCP' ? (
                        <>
                          <Typography sizes={['label']}>
                            {t('campaignPageList.firstPublication')}
                            {': '}
                            {beat.date_start ? dayjs(beat.date_start).format('YYYY-MM-DD') : '-'}
                          </Typography>
                          {beat.date_start &&
                            beat.date_end &&
                            !dayjs(beat.date_start).isSame(dayjs(beat.date_end), 'day') && (
                              <>
                                <br />
                                <Typography sizes={['label']}>
                                  {t('campaignPageList.lastPublication')}
                                  {': '}
                                  {dayjs(beat.date_end).format('YYYY-MM-DD')}
                                </Typography>
                              </>
                            )}
                        </>
                      ) : (
                        <>
                          <Typography sizes={['label']}>
                            {t('campaignPageList.beatStart')}
                            {': '}
                            {beat.date_start ? dayjs(beat.date_start).format('YYYY-MM-DD') : '-'}
                          </Typography>
                          <br />
                          <Typography sizes={['label']}>
                            {t('campaignPageList.beatEnd')}
                            {': '}
                            {beat.date_end ? dayjs(beat.date_end).format('YYYY-MM-DD') : '-'}
                          </Typography>
                        </>
                      )}
                    </div>

                    <Tags
                      tags={[
                        {
                          value: t(`status.${beat.status}`),
                          theme: beat.status
                        }
                      ]}
                    />
                  </div>
                </div>
              </div>
            </TranslatedLink>
          ))}
        </div>
      )}

      {!!memoBriefPdfs.length && (
        <>
          <div className={styles.campaignPageListFilesHeading}>
            <Typography sizes={['h4']}>{t('campaignPageList.headingFiles')}</Typography>
          </div>

          {memoBriefPdfs.map((file) => (
            <Link href={file.url} key={`beat-file-${file.url}`} target="_blank">
              <div className={styles.campaignPageListFilesItem}>
                <Download size={16} />

                <Typography sizes={['bold']}>{file.filename}</Typography>
              </div>
            </Link>
          ))}
        </>
      )}
    </Wrapper>
  );
}
